import * as React from "react";
import { useAuth } from "../../../context/AuthContext";
import { legeTodoImVorgangAn } from "./todoImVorgangAnlegen";

/**
 * F3 (PO 01.10.2026): EIN Vorgang mit To-dos als aufklappbare Zeile — die
 * Ausklapp-Logik der Mehrfachauswahl (`VorgangMehrfachPicker`), auf diesen
 * einen Vorgang beschränkt. Für die Nach-Anruf-Karte: Der eindeutig passende
 * Vorgang des Kontakts wird vorgeschlagen, der Nutzer klappt auf, hakt
 * To-dos an (oder legt per „Neues To-do…" eines an) und ordnet zu.
 *
 * Dieselben Regeln wie dort: ein Vorgang MIT To-dos ist selbst kein Ziel;
 * ein neues To-do entsteht erst beim Zuordnen, und VOR dem Zuordnen.
 */
export interface VorgangTodoAuswahlProps {
    vorgangId: number;
    /** Anzeige des Vorgangs, z. B. „4711-2 · Kfz-Schaden". */
    titel: string;
    todos: Array<{ id: number; titel: string | null; status?: string | null }>;
    /** Vorbelegt angehakt (z. B. das To-do, aus dem gewählt wurde). */
    vorbelegtTodoId?: number | null;
    /** Startet aufgeklappt. */
    offen?: boolean;
    knopfText?: string;
    onZuordnen: (ziele: Array<{ vorgangId: number; todoId: number }>) => Promise<void>;
    disabled?: boolean;
}

export const VorgangTodoAuswahl: React.FC<VorgangTodoAuswahlProps> = ({
    vorgangId,
    titel,
    todos,
    vorbelegtTodoId,
    offen: offenStart = false,
    knopfText = "Zuordnen",
    onZuordnen,
    disabled,
}) => {
    const { user } = useAuth();
    const [offen, setOffen] = React.useState(offenStart);
    const [gewaehlt, setGewaehlt] = React.useState<Set<number>>(
        () => new Set(vorbelegtTodoId != null ? [vorbelegtTodoId] : []),
    );
    const [neuOffen, setNeuOffen] = React.useState(false);
    const [neuTitel, setNeuTitel] = React.useState("");
    /** Schon angelegt (bei einem Fehler danach nicht doppelt anlegen). */
    const [angelegt, setAngelegt] = React.useState<{ id: number; titel: string } | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [fehler, setFehler] = React.useState("");

    const anzahl = gewaehlt.size + (neuTitel.trim() && !angelegt ? 1 : 0) + (angelegt ? 1 : 0);

    const toggle = (id: number) =>
        setGewaehlt((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const zuordnen = async () => {
        setBusy(true);
        setFehler("");
        try {
            let neu = angelegt;
            if (!neu && neuTitel.trim()) {
                try {
                    neu = await legeTodoImVorgangAn({ vorgangId, titel: neuTitel, currentUserId: user?.id ?? null });
                } catch (error) {
                    setFehler(
                        (error as { status?: number } | null)?.status === 403
                            ? "Kein Recht, in diesem Vorgang ein To-do anzulegen."
                            : "Das To-do konnte nicht angelegt werden.",
                    );
                    return;
                }
                setAngelegt(neu);
            }
            const ids = [...gewaehlt, ...(neu ? [neu.id] : [])];
            if (ids.length === 0) return;
            await onZuordnen(ids.map((todoId) => ({ vorgangId, todoId })));
        } catch {
            setFehler("Zuordnung fehlgeschlagen. Bitte erneut versuchen.");
        } finally {
            setBusy(false);
        }
    };

    const gesperrt = busy || !!disabled;

    return (
        <div className="vta">
            <button
                type="button"
                className="callw-btn on vta-kopf"
                aria-expanded={offen}
                onClick={() => setOffen((o) => !o)}
                title={`${todos.length} ${todos.length === 1 ? "To-do" : "To-dos"} – zum Anhaken aufklappen`}
            >
                <span className="vta-titel">{titel}</span>
                <span className="vta-zahl">
                    {todos.length} {todos.length === 1 ? "To-do" : "To-dos"} {offen ? "▴" : "▾"}
                </span>
            </button>
            {offen && (
                <div className="vta-liste">
                    {todos.map((t) => (
                        <label key={t.id} className="vta-zeile">
                            <input
                                type="checkbox"
                                checked={gewaehlt.has(t.id)}
                                disabled={gesperrt}
                                onChange={() => toggle(t.id)}
                                aria-label={`Todo ${t.titel ?? t.id}`}
                            />
                            <span className="vta-todo">↳ {t.titel || `To-do #${t.id}`}</span>
                            {t.status && <span className="vta-status">{t.status}</span>}
                        </label>
                    ))}
                    {angelegt && (
                        <label className="vta-zeile">
                            <input type="checkbox" checked disabled aria-label={`Todo ${angelegt.titel}`} />
                            <span className="vta-todo">↳ {angelegt.titel}</span>
                        </label>
                    )}
                    {!angelegt &&
                        (neuOffen ? (
                            <input
                                className="vta-neu-feld"
                                autoFocus
                                aria-label="Betreff des neuen To-dos"
                                placeholder="Betreff des neuen To-dos"
                                value={neuTitel}
                                disabled={gesperrt}
                                onChange={(e) => setNeuTitel(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                        e.stopPropagation();
                                        setNeuTitel("");
                                        setNeuOffen(false);
                                    }
                                }}
                            />
                        ) : (
                            <button
                                type="button"
                                className="callw-tool callw-tool-klein vta-neu"
                                disabled={gesperrt}
                                onClick={() => setNeuOffen(true)}
                            >
                                + Neues To-do…
                            </button>
                        ))}
                    <button
                        type="button"
                        className="callw-btn on"
                        disabled={gesperrt || anzahl === 0}
                        onClick={() => void zuordnen()}
                    >
                        {busy ? "Wird zugeordnet…" : anzahl > 1 ? `${anzahl} To-dos zuordnen` : knopfText}
                    </button>
                    {fehler && <div className="callw-fehler">{fehler}</div>}
                </div>
            )}
        </div>
    );
};

export default VorgangTodoAuswahl;
